/*
 * From https://www.redblobgames.com/plotter/2229-svg-logo/
 * Copyright 2022 Red Blob Games <redblobgames@gmail.com>
 * License: Apache-2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 *
 * Algorithm based on code I wrote in 2008 for
 * http://www-cs-students.stanford.edu/~amitp/_test/facecontrol.swf
 */

function lerp(a: number, b: number, t: number): number { return a * (1-t) + b * t; }
function randRange(lo: number, hi: number): number { return Math.floor(Math.random() * (hi-lo)) + lo; }
function randInt(lo: number, hi: number): number { return randRange(lo, hi+1); }


function Point(x: number, y: number) {
    return {x, y, toString() { return `${this.x},${this.y}`; } };
}
type Point = {x: number, y: number};

/** Intersection of ray p-->u and q-->v */
function intersection(p: Point, u: Point, q: Point, v: Point): Point {
    let v_cross_u = v.x*u.y - v.y*u.x;
    if (Math.abs(v_cross_u) <= 1e-4) {
        // Lines are parallel, or close to it, so use the midpoint
        return Point(0.5*(p.x+q.x), 0.5*(p.y+q.y));
    }

    // Lines are not parallel, so compute the intersection, using the algorithm on
    // http://geometryalgorithms.com/Archive/algorithm_0104/algorithm_0104B.htm
    let w = Point(p.x - q.x, p.y - q.y);
    let v_cross_w = v.x*w.y - v.y*w.x;
    let s = -v_cross_w / v_cross_u;
    return Point(p.x + u.x * s, p.y + u.y * s);
}

function evaluateQuadraticAt(P: Point, Q: Point, R: Point, t: number): Point {
    return Point(
        (1-t)**2 * P.x + 2*(1-t)*t * Q.x + t**2 * R.x,
        (1-t)**2 * P.y + 2*(1-t)*t * Q.y + t**2 * R.y
    );
}

/** return t for desired x or y */
function solveQuadratic(P: Point, Q: Point, R: Point, goal: number, axis='x'): number | null {
    const a = P[axis] - 2 * Q[axis] + R[axis];
    const b = -2 * P[axis] + 2 * Q[axis];
    const c = P[axis] - goal;
    let m = Math.sqrt(b**2 - 4*a*c);
    // NOTE: there's also Muller's formula, which is better in some circumstances;
    // see <https://people.csail.mit.edu/bkph/articles/Quadratics.pdf>; however
    // for these particular curves Muller's formula doesn't seem any better.
    let t1 = (-b + m) / (2*a);
    let t2 = (-b - m) / (2*a);
    // The curves I'm dealing with should have only one of these in range
    const ε = 1e-6;
    let t1InRange = 0-ε <= t1 && t1 <= 1+ε;
    let t2InRange = 0-ε <= t2 && t2 <= 1+ε;
    if (t1InRange && t2InRange && Math.abs(t1 - t2) > ε) {
        console.log(`WARNING: quadratic equation produced two solutions ${t1} and ${t2}`);
    }

    if (t1InRange) return t1;
    if (t2InRange) return t2;
    return null;
}

/* The svg version has some limitations:

   - no 't' parameter
   - no mouth skew
   - no mouth rotate

   This one is designed for plotter use so it doesn't use fills or 
   clipping.
*/

const FaceGenerator = {
    shape: {
        m: 1.9, p: 0.3, q: 0.5, r: 0.8, s: 0.6,
        browLift: 0.5, browAngle: 1,
        eyeOffsetX: 10, eyeOffsetY: 20, eyeSymmetricOffset: true
    },
    mouthSize: 15,
    
    face(): string {
        return `
<g fill="none" transform="scale(1.5, 1.5)">
  ${this.eyes()}
  ${this.jaw()}
</g>`;
    },

    eyes() {
        return `
<g transform="translate(-100, 20)" stroke="black" stroke-width="1">
  <g transform="translate(200, 0)">
    ${this.eyebrows(Point(200, 0))}
  </g>
  <g transform="translate(200, 50)">
    ${this.eyeballs(30, 40, 6, 8)}
  </g>
</g>`;
    },

    eyebrows() {
        const {browLift, browAngle} = this.shape;
        return `
<path d="M -50,${-15*browLift + 10*browAngle} l 40,${-20*browAngle}" />
<path d="M 10,${-15*browLift - 10*browAngle} l 40,${20*browAngle}" />`;
    },

    eyeballs(width: number, height: number, spacing: number, size: number) {
        const {eyeOffsetX, eyeOffsetY, eyeSymmetricOffset} = this.shape;
        const dx = width + spacing/2;
        const sign = eyeSymmetricOffset ? -1 : +1;

        // TODO: steps should depend on MM size
        let ySteps = Math.round(size/1.5) / 4;
        ySteps *= 4;
        let svgEye = ``;
        for (let j = -ySteps; j <= ySteps; j++) {
            let y = lerp(0, size, j/ySteps);
            if (isNaN(y)) y = 0;
            let x = Math.sqrt(size**2 - y**2);
            svgEye += `<line x1="${-x}" x2="${x}" y1="${y}" y2="${y}" />`;
        }
        
        let svg = ``;
        svg += `<g transform="translate(${-dx + eyeOffsetX*sign}, ${eyeOffsetY})">${svgEye}</g>`;
        svg += `<g transform="translate(${dx + eyeOffsetX}, ${eyeOffsetY})">${svgEye}</g>`;
        svg += `
  <ellipse cx="${-dx}" rx="${width}" ry="${height}" />
  <ellipse cx="${dx}" rx="${width}" ry="${height}" />
  <circle cx="${-dx + eyeOffsetX*sign}" cy="${eyeOffsetY}" r="${size}" />
  <circle cx="${dx + eyeOffsetX}" cy="${eyeOffsetY}" r="${size}" />
`;

        return svg;
    },

    jaw() {
        const {shape} = this;

        return `
<g transform="translate(100, 120) scale(1.5, 1.5)">
  <g transform="scale(${this.mouthSize},${this.mouthSize})">
    ${this.mouth()}
</g>
  <g transform="scale(${this.mouthSize},${this.mouthSize})" fill-opacity="0.5">
${this.teeth(0, 1 + shape.m, 5, 0.5, 1)}
  </g>
</g>`;
    },

    teeth(upperY: number, lowerY: number, halfNumTeeth: number, toothWidth: number, toothHeight: number) {
        let svg = ``;

        function findPointOn(lip: Point[][], value: number, axis: string): Point | null {
            for (let [P, Q, R] of lip) {
                let t = solveQuadratic(P, Q, R, value, axis);
                if (t !== null) {
                    return evaluateQuadraticAt(P, Q, R, t);
                }
            }
            return null;
        }

        // Draw vertical lines
        let {lowerLip, upperLip, full} = this.mouthShapePoints();
        for (let i = -halfNumTeeth; i <= halfNumTeeth; i++) {
            let x = toothWidth * i;
            if (x < -2 || x > +2) continue;
            let upperLipY = findPointOn(upperLip, x, 'x')?.y ?? -Infinity;
            let lowerLipY = findPointOn(lowerLip, x, 'x')?.y ?? +Infinity;
            for (let toothStartY of [upperY, lowerY]) {
                let segment = [Math.max(upperLipY, toothStartY),
                               Math.min(lowerLipY, toothStartY + toothHeight)];
                if (segment[1] >= segment[0]) {
                    svg += `<line x1="${x}" x2="${x}" y1="${segment[0]}" y2="${segment[1]}" />`;
                }
            }
        }

        // Draw horizontal lines
        let STEPS = 5; // per svg unit, in mouth coords
        STEPS = 15;
        let yRange = [upperY + toothHeight, lowerY];
        let ySteps = Math.round(STEPS * (yRange[1] - yRange[0]));
        for (let j = 0; j <= ySteps; j++) {
            let y = lerp(yRange[0], yRange[1], j/ySteps);
            if (isNaN(y)) y = (yRange[0] + yRange[1]) / 2;
            let x = full.map(lip => findPointOn([lip], y, 'y')?.x).filter(y => y !== undefined);
            x.sort((a, b) => a - b);
            for (let i = 0; i < x.length; i += 2) {
                svg += `<line x1="${x[i]}" x2="${x[i+1]}" y1="${y}" y2="${y}" />`;
            }
        }

        return `
<g stroke="black" stroke-width="0.04">
  ${svg}
</g>`;
    },

    mouth() {
        let {full} = this.mouthShapePoints();
        let d = `M ${full[0][0]}`;
        for (let q of full) { d += ` Q ${q[1]} ${q[2]}`; }

        return `
<g stroke="black" stroke-width="0.04">
  <path d="${d}" />
</g>`;
    },

    mouthShapePoints() {
        const {shape} = this;

        // The names are from 2008: A, E are the right and left corners;
        // C is the top; G is the bottom. There used to be B, D, F, H too.
        
        let mouthCorner = (2+shape.m)*(1-shape.s)
        let Ap = Point(2, mouthCorner);
        let Cp = Point(0, 1-shape.p);
        let Ep = Point(-2, mouthCorner);
        let Gp = Point(0, 1+shape.m+shape.q);
        
        let Av1 = Point(0.5 * (1-shape.r) * (Cp.x - Ap.x), Cp.y - Ap.y);
        let Av2 = Point(0.5 * (1-shape.r) * (Ap.x - Gp.x), Ap.y - Gp.y);
        let Ev1 = Point(0.5 * (1-shape.r) * (Gp.x - Ep.x), Gp.y - Ep.y);
        let Ev2 = Point(0.5 * (1-shape.r) * (Ep.x - Cp.x), Ep.y - Cp.y);

        let horizontal = Point(1, 0);

        let EC = intersection(Ep, Ev2, Cp, horizontal);
        let CA = intersection(Cp, horizontal, Ap, Av1);
        let EG = intersection(Ep, Ev1, Gp, horizontal);
        let GA = intersection(Gp, horizontal, Ap, Av2);
        return {
            full: [[Ep, EC, Cp], [Cp, CA, Ap], [Ap, GA, Gp], [Gp, EG, Ep]],
            upperLip: [[Ep, EC, Cp], [Cp, CA, Ap]],
            lowerLip: [[Ep, EG, Gp], [Gp, GA, Ap]],
        };
    },
};

const BlobGenerator = {
    path(flex: number, radiusX: number, radiusY: number): string {
        const steps = Math.ceil(Math.max(radiusX, radiusY) * 2 * Math.PI / 10);
        let path = [];
        for (let i = 0; i <= steps; i++) {
            let θ = 2 * Math.PI * i / steps;
            let r = (flex + Math.sin(5 * θ)) / (1 + flex);
            let x =  r * radiusX * Math.cos(θ);
            let y = -r * radiusY * Math.sin(θ);
            path.push({θ, r, x, y});
        }
        let svg = `<path d="M ${path.map(({x, y}) => x.toFixed(2)+","+y.toFixed(2)).join(" L ")} z" />`;
        return svg;
    },
};
    

const MM_PER_INCH = 25.4;
const AXIDRAW_PER_MM = 80;
const PAPER_IN = {w: 11, h: 8.5};
const MARGIN_IN = {w: 1, h: 1};
const OUTER = {
    w: PAPER_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    h: PAPER_IN.h * MM_PER_INCH * AXIDRAW_PER_MM
};
const INNER = {
    left: MARGIN_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    top: MARGIN_IN.h * MM_PER_INCH * AXIDRAW_PER_MM,
    w: OUTER.w - 2*MARGIN_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    h: OUTER.h - 2*MARGIN_IN.h * MM_PER_INCH * AXIDRAW_PER_MM
};
const FILL_FRACTION = 0.9; // how much of the allocated region to fill
const COLS = 8;
const PEN_WIDTH = 12; /* what I see on screen doesn't match the plotter; need to experiment */
let generator = {
    // NOTE: a lot of this code is copied from my modulo circle plotter code
    parameters: [], // [{col, row, x, y}, …]
    svg: [], // one svg for each item in parameters

    init() {
        this.cols = COLS;
        this.rows = Math.floor(INNER.h / INNER.w * COLS);
        this.spacing = (INNER.w - 2*MARGIN_IN.w) / COLS;
        
        for (let col = 0; col < this.cols; col++) {
            for (let row = 0; row < this.rows; row++) {
                let x = MARGIN_IN.w * MM_PER_INCH * AXIDRAW_PER_MM + (col + 0.5) * this.spacing,
                    y = MARGIN_IN.h * MM_PER_INCH * AXIDRAW_PER_MM + (row + 0.5) * this.spacing;
                this.parameters.push({col, row, x, y});
                // this.generateParameters(this.parameters.length-1);
            }
        }

        this.generateGrid();
    },

    generateGrid() {
        const {spacing} = this;
        let svg = [];
        for (let i = 0; i < this.parameters.length; i++) {
            let {x, y} = this.parameters[i];
            randomize();
            svg.push(`
<g transform="translate(${x},${y})" onClick="regenerate(${i})">
<g transform="scale(${spacing},${spacing}) translate(-0.5,-0.49) scale(${1/400},${1/400})">
  ${generate()}
</g>
</g>
`);
        }
        this.svg = svg;
    },
}


function randomize() {
    const shape = FaceGenerator.shape;
    shape.p = lerp(-1, 1, Math.random());
    shape.q = lerp(-1, 1, Math.random());
    shape.r = lerp(0, 1, Math.random());
    shape.s = lerp(0, 1, Math.random());
    shape.m = lerp(0, 1.5, Math.random());

    shape.browLift = lerp(-0.5, 1, Math.random());
    shape.browAngle = lerp(-1, 1, Math.random());

    shape.eyeOffsetX = lerp(-10, 10, Math.random());
    shape.eyeOffsetY = lerp(-15, 15, Math.random());
    shape.eyeSymmetricOffset = shape.eyeOffsetX < 0 && Math.random() < 0.2;

    fixOverlap();
}

function fixOverlap() {
    // This is an unsolved problem with my parameterization
    const shape = FaceGenerator.shape;
    let overlap = -(shape.m + shape.q + shape.p);
    if (overlap > 0) {
        shape.p += overlap/2 + 0.1;
        shape.q += overlap/2 + 0.1;
    }
}

function generate() {
    // generate a blob using the current (global) parameters in
    // FaceGenerator.shape; it will be roughly 400x400
    return `
<g transform="translate(125, 10) scale(0.5, 0.5)">
  ${FaceGenerator.face()}
</g>
<g transform="translate(200, 200)" fill="none" stroke="black" stroke-width="0.75">
  ${BlobGenerator.path(5, 199, 199)}
</g>
`;
}

function reload() {
    randomize();

    // Some problematic mouth shapes, now working but keeping in case I need to test again:
    // Object.assign(FaceGenerator.shape, {m: 0.34, p: -0.66, q: 0.77, r: 0.35, s: 0.36});
    // Object.assign(FaceGenerator.shape, {m: 0.32, p: -0.02, q: -0.1, r: 0.3, s: 0.2});
    // Object.assign(FaceGenerator.shape, {m: 0.02, p: -0.87, q: -0.62, r: 0.52, s: 0.37});
    
    fixOverlap();
    console.log("Shape:", JSON.stringify(FaceGenerator.shape));
    document.querySelector("#output").innerHTML = generate();
}

reload();
document.querySelector("#reload").addEventListener('click', reload);


generator.init();
generator.generateGrid();
const svgEl = document.querySelector("#grid-output");
const preEl = document.querySelector("#grid-source") as HTMLTextAreaElement;
const svg = generator.svg.join("\n");
svgEl.setAttribute('viewBox', `0 0 ${OUTER.w} ${OUTER.h}`);
svgEl.innerHTML = svg;
preEl.value =  `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg viewBox="0 0 ${OUTER.w} ${OUTER.h}" width="${PAPER_IN.w}in" height="${PAPER_IN.h}in">
  <g fill="none" stroke="black" stroke-width="${0.5 * AXIDRAW_PER_MM}">
    ${svg}
  </g>
</svg>
`;

